// src/components/Pages/RefactorLabPage.js

import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    Box, Container, Title, Text, Paper, Group, Button, FileInput, Stack, Alert,
    Code, Badge, Tabs, Divider, ThemeIcon, useMantineTheme,
    useMantineColorScheme, Grid, Accordion, Tooltip, Anchor,
    Center, RingProgress, ActionIcon, Collapse, NumberInput,
    SegmentedControl, TextInput, Loader, Stepper
} from '@mantine/core';
import {
    IconFlask, IconCloudUpload, IconAlertCircle, IconCode, IconCheck,
    IconLayoutCards, IconUser, IconCalendar, IconRobot,
    IconShieldX, IconInfoCircle, IconExternalLink,
    IconSparkles, IconDatabaseImport, IconMicroscope, IconSearch, IconAlertTriangle,
    IconMap2, IconTerminal, IconTimeline, IconListNumbers, IconBrandGoogle, IconBrain,
    IconDatabase, IconFileText, IconArrowRight, IconHistory, IconActivity
} from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useAuth } from '../../context/AuthContext';

// --- CONFIGURATION ---
const REFACTOR_ENDPOINT = 'https://us-central1-freelancer-ideas-portal.cloudfunctions.net/run_refactor_pipeline';
const CURRENT_YEAR = new Date().getFullYear();

// --- HELPER: Humanize Technical Methods ---
const safeRender = (data) => {
    if (data === null || data === undefined) return null;
    if (typeof data === 'string' || typeof data === 'number') return data;
    if (typeof data === 'boolean') return data ? "True" : "False";
    if (typeof data === 'object') {
        if (data.analysis) return JSON.stringify(data.analysis);
        if (data.reasoning) return JSON.stringify(data.reasoning);
        if (data.executive_brief) return JSON.stringify(data.executive_brief);
        return JSON.stringify(data, null, 2);
    }
    return String(data);
};

// --- HELPER: Date & Time Formatter ---
const formatDate = (isoString) => {
    if (!isoString) return 'Unknown Date';
    try {
        const date = new Date(isoString);
        return new Intl.DateTimeFormat('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true // Ensures "3:45 PM" format
        }).format(date);
    } catch (e) {
        return String(isoString);
    }
};

// --- HELPER: The "Trust Protocol" Badge Logic (v2026.4) ---
// Updated to handle "Stale" overrides
const getStatusConfig = (badge, isStale = false) => {
    if (!badge) return { color: 'gray', icon: <IconInfoCircle size={20} />, label: 'PENDING', bg: 'gray.0' };

    const b = (typeof badge === 'string') ? badge.toUpperCase() : 'UNKNOWN';

    // 1. REFUTED (High Priority)
    if (b.includes('REFUTED') || b === 'CONFLICT_FOUND') return { color: 'red', icon: <IconShieldX size={20} />, label: 'REFUTED', bg: 'red.0' };

    // 2. FRESH DATA (Nuanced)
    if (b === 'FRESH_DATA_FOUND' || b === 'UPDATE_FOUND') {
        // OVERRIDE: If data is chronologically old, downgrade the excitement
        if (isStale) {
            return { color: 'blue', icon: <IconDatabaseImport size={20} />, label: 'DATA FOUND', bg: 'blue.0' };
        }
        return { color: 'grape', icon: <IconSparkles size={20} />, label: 'FRESH DATA FOUND', bg: 'grape.0' };
    }

    // 3. VERIFIED
    if (b === 'VERIFIED_MATCH' || b === 'VERIFIED') return { color: 'green', icon: <IconCheck size={20} />, label: 'VERIFIED MATCH', bg: 'green.0' };

    // 4. REVIEW
    if (b.includes('REVIEW')) return { color: 'orange', icon: <IconAlertTriangle size={20} />, label: 'REVIEW REQUIRED', bg: 'orange.0' };

    // 5. DEFAULT
    return { color: 'gray', icon: <IconSearch size={20} />, label: 'UNSUBSTANTIATED', bg: 'gray.0' };
};

// --- COMPONENT: Strategist Brief ---
const StrategistBrief = ({ strategy }) => {
    const [isOpen, setIsOpen] = useState(false);
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';

    if (!strategy) return null;
    let briefText = "Strategy analysis available.";
    let detailedText = "";
    if (typeof strategy === 'object') {
        briefText = safeRender(strategy.executive_brief) || "Strategy Data Available";
        detailedText = safeRender(strategy.comprehensive_strategy) || JSON.stringify(strategy, null, 2);
    } else {
        briefText = String(strategy);
    }

    return (
        <Alert icon={<IconMap2 size={24} />} title="Research Strategy" color="violet" variant="light" mb="xl" radius="md">
            <Stack spacing="sm">
                <Text size="sm" fw={500} style={{ lineHeight: 1.6 }}>{briefText}</Text>
                <Box>
                    <Button variant="subtle" color="violet" size="xs" onClick={() => setIsOpen(!isOpen)}>
                        {isOpen ? "Hide Comprehensive Analysis" : "View Comprehensive Strategy"}
                    </Button>
                    <Collapse in={isOpen}>
                        <Box mt="sm" p="md" bg={isDark ? 'dark.6' : 'white'} style={{ borderRadius: 8, border: isDark ? '1px solid #333' : '1px solid #eee' }}>
                            <Text size="xs" c="dimmed" tt="uppercase" fw={700} mb="xs">Full Analysis</Text>
                            <Text size="sm" style={{ whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: '12px', lineHeight: 1.5 }}>
                                {detailedText}
                            </Text>
                        </Box>
                    </Collapse>
                </Box>
            </Stack>
        </Alert>
    );
};

// --- COMPONENT: Evidence Block ---
const EvidenceBlock = ({ evidence, isFreshData }) => {
    const [isOpen, setIsOpen] = useState(false);
    if (!evidence || !Array.isArray(evidence) || evidence.length === 0) return null;

    const primaryProof = evidence[0];
    const secondaryProofs = evidence.slice(1);

    const getSourceDisplay = (proof) => {
        if (proof.publisher && proof.publisher !== "Unknown") return safeRender(proof.publisher);
        try { return new URL(proof.source_url).hostname.replace('www.', ''); } catch { return 'External Source'; }
    };

    return (
        <Box mt="sm">
            <Paper p="sm" radius="sm" withBorder bg="gray.0" style={{ borderColor: isFreshData ? '#E599F7' : undefined }}>
                <Group position="apart" align="flex-start" wrap="nowrap">
                    <Group gap="sm" wrap="nowrap" align="flex-start">
                        <ThemeIcon size="md" color={isFreshData ? "grape" : "blue"} variant="light" mt={2}><IconDatabaseImport size={16} /></ThemeIcon>
                        <Box>
                            <Group spacing="xs">
                                <Text size="xs" fw={700} c="dimmed" tt="uppercase">{isFreshData ? "New Data Found" : "Primary Source"}</Text>
                                {primaryProof.data_year && <Badge size="xs" variant="outline" color="dark">Year: {safeRender(primaryProof.data_year)}</Badge>}
                            </Group>
                            <Text size="md" fw={600} color={isFreshData ? "grape.9" : "dark.9"} mt={4}>{safeRender(primaryProof.extracted_value)}</Text>
                            {primaryProof.quote && <Text size="sm" c="dimmed" lineClamp={3} mt={4} style={{ lineHeight: 1.4, fontStyle: 'italic' }}>"{safeRender(primaryProof.quote)}"</Text>}
                        </Box>
                    </Group>
                </Group>
                <Group mt="sm" spacing="xs" position="apart">
                    <Group spacing="xs">
                        <Badge size="sm" variant="filled" color={isFreshData ? "grape" : "blue"}>{getSourceDisplay(primaryProof)}</Badge>
                        {primaryProof.source_url && <Anchor href={primaryProof.source_url} target="_blank" size="xs" c="dimmed" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>View Source <IconExternalLink size={12} /></Anchor>}
                    </Group>
                </Group>
            </Paper>

            {secondaryProofs.length > 0 && (
                <Box mt="xs">
                    <Button variant="subtle" size="xs" color="gray" onClick={() => setIsOpen(!isOpen)} leftSection={<IconSearch size={12} />}>{isOpen ? 'Hide' : `View ${secondaryProofs.length} More Sources`}</Button>
                    <Collapse in={isOpen}>
                        <Stack spacing="xs" mt="xs" pl="md" style={{ borderLeft: '2px solid #eee' }}>
                            {secondaryProofs.map((proof, idx) => (
                                <Box key={idx}>
                                    <Text size="xs" fw={700}>{getSourceDisplay(proof)} ({safeRender(proof.data_year) || 'N/A'})</Text>
                                    <Text size="xs" c="dimmed" lineClamp={2}>{safeRender(proof.extracted_value)}</Text>
                                    {proof.source_url && <Anchor href={proof.source_url} target="_blank" size="xs">Link</Anchor>}
                                </Box>
                            ))}
                        </Stack>
                    </Collapse>
                </Box>
            )}
        </Box>
    );
};

// --- COMPONENT: Claim Card ---
const ClaimCard = ({ claim, index }) => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';

    // 1. Time Awareness Logic
    const primaryEvidence = claim.evidence && claim.evidence[0];
    const rawYear = primaryEvidence ? primaryEvidence.data_year : null;
    // Extract integer from year (handles "2023 (Est)" or "2023")
    const cleanYear = rawYear ? parseInt(String(rawYear).replace(/\D/g, '')) : null;

    // Threshold: If year is < 2025 (i.e. 2024 or older), it's Stale.
    const isStale = cleanYear && cleanYear < (CURRENT_YEAR - 1);

    // 2. Get Config with Override
    const config = getStatusConfig(claim.verification_badge, isStale);

    // Is it *visually* fresh? (Only if badge says so AND it's not stale)
    const isFreshVisual = (claim.verification_badge === 'FRESH_DATA_FOUND' || claim.verification_badge === 'UPDATE_FOUND') && !isStale;

    const handleGoogleVerify = () => window.open(`https://google.com/search?q=${encodeURIComponent(safeRender(claim.telemetry?.query) || safeRender(claim.claim))}`, '_blank');
    const handleDeepVerify = () => window.open(`https://g.ai/?q=${encodeURIComponent(safeRender(claim.telemetry?.query) || safeRender(claim.claim))}`, '_blank');

    const researchNotesText = (typeof claim.research_notes === 'object') ? (claim.research_notes.analysis || JSON.stringify(claim.research_notes)) : claim.research_notes;

    // 3. Ghost Mode Styles (for Stale data)
    const ghostStyles = isStale ? {
        opacity: 0.85,
        filter: isDark ? 'grayscale(0.3)' : 'grayscale(0.1)',
        backgroundColor: isDark ? theme.colors.dark[9] : theme.colors.gray[2] // Stale cards slightly darker than normal ones
    } : {};

    return (
        <Paper
            withBorder
            radius="md"
            p="lg"
            mb="lg"
            shadow="sm"
            style={{
                borderColor: isFreshVisual ? theme.colors.grape[4] : (isDark ? theme.colors.dark[3] : theme.colors.gray[3]),
                backgroundColor: isDark ? theme.colors.dark[9] : theme.white,
                transition: 'all 0.2s ease',
                ...ghostStyles
            }}
        >
            <Group position="apart" align="center" mb="md" wrap="nowrap">
                <Group gap="sm" align="center">
                    <ThemeIcon color={config.color} size="xl" radius="md" variant="light">{config.icon}</ThemeIcon>
                    <Badge color={config.color} variant="filled" size="md">{config.label}</Badge>

                    {/* The Stale Warning Badge */}
                    {isStale && (
                        <Badge color="red" variant="filled" size="lg" leftSection={<IconHistory size={12} />}>
                            CAUTION: INCLUDES STALE DATA
                        </Badge>
                    )}
                </Group>
            </Group>

            <Text size="md" weight={600} style={{ lineHeight: 1.5 }} mb="sm" color={isDark ? 'gray.1' : 'dark.9'}>"{safeRender(claim.claim)}"</Text>

            {claim.telemetry && (
                <Alert variant="filled" color={isDark ? "dark" : "gray"} p="xs" radius="sm" mb="sm" styles={{ root: { backgroundColor: isDark ? theme.colors.dark[6] : theme.colors.gray[1] }, message: { color: isDark ? theme.colors.gray[3] : theme.colors.dark[4] } }}>
                    <Group spacing="xs" position="apart">
                        <Group spacing="xs" wrap="nowrap">
                            <ThemeIcon size="xs" color="gray" variant="transparent"><IconTerminal size={14} /></ThemeIcon>
                            <Text size="xs" ff="monospace" lineClamp={1} title={safeRender(claim.telemetry.query)}>Query: {safeRender(claim.telemetry.query)}</Text>
                        </Group>
                        <Group spacing={6}>
                            <Button variant="light" color="blue" size="xs" onClick={handleGoogleVerify} leftSection={<IconBrandGoogle size={12} />}>Verify</Button>
                            <Button variant="light" color="gray" size="xs" onClick={handleDeepVerify} leftSection={<IconBrain size={12} />}>Try g.AI</Button>
                        </Group>
                    </Group>
                </Alert>
            )}
            {researchNotesText && (
                <Alert icon={<IconRobot size={16} />} color={config.color} variant="light" p="sm" radius="sm" styles={{ message: { fontSize: '14px', lineHeight: 1.5 } }}>
                    <Text fw={700} size="xs" tt="uppercase" mb={4} c="dimmed">Executive Summary</Text>
                    <Box style={{ whiteSpace: 'pre-wrap' }}>{researchNotesText}</Box>
                </Alert>
            )}
            {(config.label !== 'UNSUBSTANTIATED' && config.label !== 'PENDING') && <EvidenceBlock evidence={claim.evidence} isFreshData={isFreshVisual} />}
        </Paper>
    );
};

const RefactorLabPage = () => {
    const theme = useMantineTheme();
    const { colorScheme } = useMantineColorScheme();
    const isDark = colorScheme === 'dark';
    const { currentUser } = useAuth();

    // --- STATE ---
    const [searchParams, setSearchParams] = useSearchParams();
    // Default to CMS mode for "Happy Path"
    const [inputMode, setInputMode] = useState('CMS'); // 'CMS' | 'FILE'

    // Inputs
    const [file, setFile] = useState(null);
    const [cmsId, setCmsId] = useState('');
    const [slideLimit, setSlideLimit] = useState(3);

    // Process States
    const [isPreviewLoading, setIsPreviewLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingStep, setLoadingStep] = useState(0); // For stepper simulation
    const [error, setError] = useState(null);

    // Data
    const [previewData, setPreviewData] = useState(null); // Gate
    const [asoResult, setAsoResult] = useState(null); // Run

    const [activeTab, setActiveTab] = useState('visual');

    // --- DEEP LINKING (Effect) ---
    useEffect(() => {
        const urlCmsId = searchParams.get('cms_id');
        if (urlCmsId && currentUser && !asoResult && !previewData && !isLoading) {
            setInputMode('CMS');
            setCmsId(urlCmsId);
            // Auto-trigger the Preview Gate
            handlePreview(urlCmsId);
        }
    }, [currentUser, searchParams]); // Run once when params or auth are ready

    // --- LOADING SIMULATOR (Effect) ---
    useEffect(() => {
        let interval;
        if (isLoading) {
            setLoadingStep(0);
            interval = setInterval(() => {
                setLoadingStep((current) => (current < 3 ? current + 1 : current));
            }, 7000); // Advance every 7 seconds
        }
        return () => clearInterval(interval);
    }, [isLoading]);

    // --- LOGIC: Total Lookups Counter ---
    const getTotalLookups = (data) => {
        if (!data || !data.slides) return 0;
        let count = 0;
        data.slides.forEach(slide => {
            if (slide.verification?.claims) {
                slide.verification.claims.forEach(claim => { if (claim.evidence) count += claim.evidence.length; });
            }
        });
        return count;
    };

    // --- ACTION: FETCH PREVIEW (The "Stateless Gate") ---
    const handlePreview = async (manualId = null) => {
        const targetId = manualId || cmsId;
        if (!targetId) return;

        setIsPreviewLoading(true);
        setError(null);
        setPreviewData(null);

        const formData = new FormData();
        formData.append('cms_id', targetId);
        formData.append('action', 'PREVIEW'); // Explicitly ask for Meta only

        try {
            const idToken = await currentUser.getIdToken();
            const response = await fetch(REFACTOR_ENDPOINT, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${idToken}` },
                body: formData
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || 'CMS Lookup Failed');

            setPreviewData(data);
            // Update URL to make it shareable
            setSearchParams({ cms_id: targetId });
        } catch (err) {
            console.error(err);
            setError(err.message);
            notifications.show({ title: 'Lookup Failed', message: err.message, color: 'red', icon: <IconAlertCircle size={16} /> });
        } finally {
            setIsPreviewLoading(false);
        }
    };

    // --- ACTION: EXECUTE PIPELINE ---
    const handleExecute = async () => {
        if (inputMode === 'FILE' && !file) return;
        if (inputMode === 'CMS' && !cmsId) return;

        setIsLoading(true);
        setError(null);
        setAsoResult(null);

        const formData = new FormData();
        formData.append('mode', 'REFACTOR');
        formData.append('limit', slideLimit.toString());
        formData.append('action', 'RUN'); // Explicit execute

        // Context Switch
        if (inputMode === 'CMS') {
            formData.append('cms_id', cmsId);
        } else {
            formData.append('file', file);
        }

        try {
            const idToken = await currentUser.getIdToken();
            const response = await fetch(REFACTOR_ENDPOINT, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${idToken}` },
                body: formData
            });
            const data = await response.json();
            if (!response.ok) throw new Error(data.message || data.error || 'Pipeline execution failed');

            setAsoResult(data);
            const totalClaims = data.slides?.reduce((acc, s) => acc + (s.verification?.claims?.length || 0), 0);
            notifications.show({ title: 'Grounding Complete', message: `Verified ${data.slides?.length || 0} slides (${totalClaims} claims).`, color: 'green', icon: <IconCheck size={16} /> });
        } catch (err) {
            console.error(err);
            setError(err.message);
            // Note: We leave the "Loading" state to false so the user sees the error
        } finally {
            setIsLoading(false);
        }
    };

    // --- RENDER HELPERS ---
    const renderSlideRow = (slide) => {
        const isIntro = slide.type === 'intro';
        if (isIntro) {
            return (
                <Paper key={slide.slide_number} withBorder p="lg" mb="xl" bg="blue.0" style={{ borderColor: theme.colors.blue[2] }}>
                    <Group>
                        <ThemeIcon size="xl" radius="xl"><IconInfoCircle size={24} /></ThemeIcon>
                        <Box>
                            <Text size="md" fw={700} c="blue.9">SLIDE {slide.slide_number}: INTRO</Text>
                            <Text size="md" c="blue.8">{safeRender(slide.raw_text)}</Text>
                        </Box>
                    </Group>
                </Paper>
            );
        }

        return (
            <Box key={slide.slide_number} mb={60}>
                <Group mb="md">
                    <Badge size="xl" color="dark" radius="sm">SLIDE {slide.slide_number}</Badge>
                    <Text size="md" c="dimmed" fw={500}>{safeRender(slide.slide_title)}</Text>
                </Group>
                <Grid gutter={40}>
                    <Grid.Col span={5}>
                        <Paper p="xl" radius="md" bg={isDark ? 'dark.7' : 'gray.1'} withBorder h="100%">
                            <Stack spacing="md">
                                <Text tt="uppercase" size="xs" fw={700} c="dimmed" style={{ letterSpacing: '0.5px' }}>Legacy Narrative</Text>
                                <Title order={3} style={{ lineHeight: 1.3, fontWeight: 800 }}>{safeRender(slide.slide_title)}</Title>
                                <Divider my="xs" />
                                <Text size="lg" style={{ fontFamily: 'Georgia, serif', lineHeight: 1.6 }} c={isDark ? 'gray.3' : 'dark.9'}>{safeRender(slide.raw_text)}</Text>
                            </Stack>
                        </Paper>
                    </Grid.Col>
                    <Grid.Col span={7}>
                        <Stack spacing={0}>
                            {(!slide.verification?.claims || slide.verification.claims.length === 0) ? (
                                <Alert color="gray" icon={<IconCheck size={16} />}>No verifiable claims detected on this slide.</Alert>
                            ) : (
                                slide.verification.claims.map((claim, idx) => <ClaimCard key={idx} claim={claim} index={idx} />)
                            )}
                        </Stack>
                    </Grid.Col>
                </Grid>
                <Divider my="xl" style={{ opacity: 0.3 }} />
            </Box>
        );
    };

    return (
        // [UPDATE]: Changed Light Mode bg from 'gray.0' to 'gray.3' for higher contrast
        <Box bg={isDark ? 'dark.8' : 'gray.3'} style={{ minHeight: '100vh' }}>
            {/* 1. BRAND HEADER */}
            <Paper p="md" radius={0} withBorder style={{ borderColor: isDark ? theme.colors.dark[4] : theme.colors.gray[4] }}>
                <Container size={1400}>
                    <Group justify="space-between" >
                        <Group>
                            <ThemeIcon size={44} radius="md" color="yellow" variant="gradient" gradient={{ from: 'yellow.9', to: 'yellow.6' }}>
                                <IconMicroscope size={26} />
                            </ThemeIcon>
                            <Box>
                                <Title order={4}>Writer Hub Refactor Lab</Title>
                                <Text size="sm" c="dimmed">Grounding & Verification Engine (v2026.11)</Text>
                            </Box>
                        </Group>
                        {asoResult && (
                            <Button variant="default" size="xs" onClick={() => { setAsoResult(null); setPreviewData(null); setSearchParams({}); }}>
                                Start New Audit
                            </Button>
                        )}
                    </Group>
                </Container>
            </Paper>

            <Container size={1400} py="xl">
                {error && (
                    <Alert icon={<IconAlertCircle size={16} />} title="System Issue" color="red" mb="lg" variant="filled">
                        {error}
                    </Alert>
                )}

                {/* 2. THE MASTER AREA (Input Cockpit) */}
                {!asoResult && !isLoading && (
                    <Box mb={50}>
                        <Center>
                            <Paper shadow="sm" radius="lg" p="xl" withBorder w={800} bg={isDark ? 'dark.8' : 'white'}>
                                <Stack spacing="md">
                                    <Group position="apart">
                                        <Text fw={700} size="lg" tt="uppercase" c="dimmed">Input Configuration</Text>
                                        <Badge variant="dot" size="lg" color="green">System Ready</Badge>
                                    </Group>

                                    {/* A. Mode Switcher (CMS FIRST) - VISUAL POLISH */}
                                    <SegmentedControl
                                        fullWidth
                                        size="md"
                                        radius="md" // Softens the corners
                                        color="violet"
                                        bg={isDark ? "dark.6" : "gray.1"} // Defines the "Track" background
                                        style={{ border: isDark ? '1px solid #373A40' : '1px solid #ced4da' }} // Adds a subtle border
                                        value={inputMode}
                                        onChange={(val) => { setInputMode(val); setPreviewData(null); }}
                                        data={[
                                            {
                                                value: 'CMS',
                                                label: (
                                                    <Center>
                                                        <IconDatabase size={16} />
                                                        <Box ml={10} fw={500}>Connect to CMS</Box>
                                                    </Center>
                                                )
                                            },
                                            {
                                                value: 'FILE',
                                                label: (
                                                    <Center>
                                                        <IconFileText size={16} />
                                                        <Box ml={10} fw={500}>Upload CSV File</Box>
                                                    </Center>
                                                )
                                            },
                                        ]}
                                    />

                                    <Divider />

                                    {/* B. Input Fields */}
                                    <Group grow align="flex-end">
                                        {inputMode === 'CMS' ? (
                                            <TextInput
                                                label="CMS Gallery ID"
                                                description="Direct Love Inc. database connection"
                                                placeholder="e.g. 533676"
                                                value={cmsId}
                                                onChange={(e) => setCmsId(e.target.value)}
                                                rightSection={isPreviewLoading ? <Loader size="xs" /> : <IconDatabase size={16} color="gray" />}
                                            />
                                        ) : (
                                            <FileInput
                                                label="Legacy Content File"
                                                description="Upload export from Old CMS"
                                                placeholder="Select .csv..."
                                                accept=".csv"
                                                value={file}
                                                onChange={setFile}
                                                icon={<IconCloudUpload size={16} />}
                                            />
                                        )}

                                        <NumberInput
                                            label="Analysis Limit"
                                            description="Max slides to verify"
                                            value={slideLimit}
                                            onChange={setSlideLimit}
                                            min={1}
                                            max={50}
                                            w={120}
                                        />
                                    </Group>

                                    {/* C. Action / Gate */}
                                    {inputMode === 'CMS' && !previewData && (
                                        <Button size="md" variant="filled" color="green" onClick={() => handlePreview()} loading={isPreviewLoading} disabled={!cmsId} mt="sm" fullWidth leftSection={<IconSearch size={18} />}>
                                            Fetch Metadata (Preview)
                                        </Button>
                                    )}

                                    {inputMode === 'FILE' && (
                                        <Button size="md" color="violet" onClick={handleExecute} disabled={!file} mt="sm" fullWidth leftSection={<IconMicroscope size={18} />}>
                                            Start Refactor Pipeline
                                        </Button>
                                    )}

                                    {/* D. THE GATE (Preview Result) */}
                                    {inputMode === 'CMS' && previewData && (
                                        <Alert icon={<IconCheck size={20} />} title="Gallery Found" color="green" variant="light" mt="md" radius="md">
                                            <Grid align="center" justify="space-between">
                                                <Grid.Col span="auto">
                                                    <Box>
                                                        <Text size="lg" fw={700} color="green.5">{safeRender(previewData.title)}</Text>
                                                        <Group spacing="xs" mt={4}>
                                                            <Badge color="green">{safeRender(previewData.brand)}</Badge>
                                                            <Text size="sm" c="dimmed">by {safeRender(previewData.author)}</Text>
                                                            <Text size="sm" c="dimmed">• {previewData.slide_count} Slides</Text>
                                                        </Group>
                                                    </Box>
                                                </Grid.Col>
                                                <Grid.Col span="content" style={{ display: 'flex', alignItems: 'center' }}>
                                                    <Button size="md" color="violet" onClick={handleExecute} leftSection={<IconArrowRight size={18} />}>
                                                        Run Analysis
                                                    </Button>
                                                </Grid.Col>
                                            </Grid>
                                        </Alert>
                                    )}
                                </Stack>
                            </Paper>
                        </Center>
                    </Box>
                )}

                {/* 3. LOADING STATE (NARRATIVE SIMULATION) */}
                {isLoading && (
                    <Center h={400}>
                        <Paper p="xl" radius="md" withBorder w={500} bg={isDark ? 'dark.7' : 'white'}>
                            <Stack align="center" spacing="xl">
                                <Group>
                                    <IconFlask size={28} className="animate-pulse" color="#f2b150" />
                                    <Text fw={700} size="lg">Running Grounding Protocol</Text>
                                </Group>
                                <Stepper active={loadingStep} orientation="vertical" size="sm">
                                    <Stepper.Step label="Handshake" description="Authenticating with Love Inc. CMS..." loading={loadingStep === 0} />
                                    <Stepper.Step label="Ingestion" description="Extracting narrative & extracting claims..." loading={loadingStep === 1} />
                                    <Stepper.Step label="Strategy" description="Constructing verification vectors..." loading={loadingStep === 2} />
                                    <Stepper.Step label="Verification" description="Grounding claims against live index..." loading={loadingStep === 3} />
                                </Stepper>
                            </Stack>
                        </Paper>
                    </Center>
                )}

                {/* 4. RESULTS DASHBOARD */}
                {asoResult && (
                    <Stack spacing="xl">
                        {/* Meta Card */}
                        <Paper p="xl" radius="md" withBorder bg={isDark ? 'dark.9' : 'white'}>
                            <Grid align="center" gutter="xl">
                                <Grid.Col span={7}>
                                    <Group mb="sm">
                                        <Badge size="xl" color="blue" variant="filled">{safeRender(asoResult.meta.brand)}</Badge>
                                        <Badge variant="outline" size="lg">Mode: {safeRender(asoResult.meta.refactor_mode)}</Badge>
                                    </Group>
                                    <Title order={2} mb="xs">{safeRender(asoResult.meta.gallery_title)}</Title>
                                    <Group spacing="xl" mt="md">
                                        <Group spacing={6}><IconUser size={18} color="gray" /><Text size="md" c="dimmed">{safeRender(asoResult.meta.author)}</Text></Group>
                                        <Group spacing={6}><IconCalendar size={18} color="gray" /><Text size="md" c="dimmed">{formatDate(asoResult.meta.publish_date_raw)}</Text></Group>
                                        <Group spacing={6}><IconListNumbers size={18} color="violet" /><Text size="md" fw={700} c="violet">{getTotalLookups(asoResult)} Sources Reviewed</Text></Group>
                                    </Group>
                                </Grid.Col>
                                <Grid.Col span={5}>
                                    <Group position="right" wrap="nowrap">
                                        <Alert variant="light" color="blue" title={`Engine Intent (Confidence: ${Math.round((asoResult.meta.classification_confidence || 0) * 100)}%)`} icon={<IconRobot size={20} />} style={{ flex: 1 }} pr="xl">
                                            {(() => {
                                                const reasoningText = typeof asoResult.meta.classification_reasoning === 'object' ? (JSON.stringify(asoResult.meta.classification_reasoning.reasoning) || JSON.stringify(asoResult.meta.classification_reasoning)) : asoResult.meta.classification_reasoning;
                                                return (
                                                    <Tooltip label={reasoningText} multiline width={300} withArrow transitionProps={{ duration: 200 }}>
                                                        <Text size="sm" lineClamp={3} style={{ lineHeight: 1.4, cursor: 'help' }}>{reasoningText}</Text>
                                                    </Tooltip>
                                                );
                                            })()}
                                        </Alert>
                                    </Group>
                                </Grid.Col>
                            </Grid>
                        </Paper>

                        {/* Strategist Brief */}
                        {asoResult.meta.search_strategy && <StrategistBrief strategy={asoResult.meta.search_strategy} />}

                        {/* Tabs - The "Controlled State" Fix (v2026.2) */}
                        <Tabs
                            value={activeTab}
                            onChange={setActiveTab}
                            variant="outline"
                            radius="md"
                            styles={(theme) => ({
                                list: {
                                    // The rail underneath the tabs
                                    borderColor: isDark ? theme.colors.dark[4] : theme.colors.gray[5],
                                    marginBottom: '0rem',
                                },
                                panel: {
                                    paddingTop: '0rem'
                                }
                            })}
                        >
                            <Tabs.List>
                                <Tabs.Tab
                                    value="visual"
                                    leftSection={<IconLayoutCards size={16} />}
                                    // --- DIRECT PROP CONTROL ---
                                    // We force the background based on React State, ignoring CSS specificity
                                    bg={activeTab === 'visual'
                                        ? (isDark ? 'blue.9' : 'blue.1')
                                        : (isDark ? 'dark.6' : 'white')
                                    }
                                    c={activeTab === 'visual'
                                        ? (isDark ? 'white' : 'blue.9')
                                        : 'dimmed' // 'dimmed' automatically handles dark/light contrast
                                    }
                                    fw={activeTab === 'visual' ? 500 : 500}
                                    style={{ transition: 'background-color 0.5s ease' }}
                                >
                                    Editorial Audit
                                </Tabs.Tab>

                                <Tabs.Tab
                                    value="json"
                                    leftSection={<IconCode size={16} />}
                                    // --- DIRECT PROP CONTROL ---
                                    bg={activeTab === 'json'
                                        ? (isDark ? 'gray.1' : 'gray.1')
                                        : (isDark ? 'dark.6' : 'white')
                                    }
                                    c={activeTab === 'json'
                                        ? (isDark ? 'black' : 'gray.9')
                                        : 'dimmed'
                                    }
                                    fw={activeTab === 'json' ? 500 : 500}
                                    style={{ transition: 'background-color 0.5s ease' }}
                                >
                                    Raw Payload
                                </Tabs.Tab>
                            </Tabs.List>

                            <Tabs.Panel value="visual">
                                {asoResult.slides.map((slide) => renderSlideRow(slide))}
                            </Tabs.Panel>
                            
                            <Tabs.Panel value="json">
                                <Code block>{JSON.stringify(asoResult, null, 2)}</Code>
                            </Tabs.Panel>
                        </Tabs>
                    </Stack>
                )}
            </Container>
        </Box>
    );
};

export default RefactorLabPage;